'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { trackEvent } from '@/lib/analytics/events';
import { apiUrl } from '@/lib/config';
import {
  ArrowLeft,
  LayoutTemplate,
  Monitor,
  Tablet,
  Smartphone,
  ExternalLink,
  Copy,
  Check,
  Sparkles,
  ArrowRight,
  Zap,
  FileCode,
  CheckCircle2,
  Lock,
  RotateCw,
  ChevronLeft,
  ChevronRight,
  Wifi,
  Battery,
  Signal,
} from 'lucide-react';

interface TemplatePreviewFrameProps {
  html: string;
  templateName: string;
  category?: string;
  editorUrl: string;
  templateSlug?: string;
  templateId?: string;
  isPremium?: boolean;
}

export default function TemplatePreviewFrame({
  html,
  templateName,
  category = 'General',
  editorUrl,
  templateSlug = 'email-template',
  templateId,
  isPremium = false,
}: TemplatePreviewFrameProps) {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [copied, setCopied] = useState(false);
  const [desktopHeight, setDesktopHeight] = useState<number>(700);
  const desktopIframeRef = useRef<HTMLIFrameElement>(null);

  // Enhanced HTML injecting smooth scrollbar & hiding horizontal overflow in previews
  const enhancedHtml = useMemo(() => {
    if (!html) return '';
    const previewStyles = `
      <style>
        ::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border-radius: 9999px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.6);
        }
        html, body {
          overflow-x: hidden !important;
        }
      </style>
    `;
    if (html.includes('</head>')) {
      return html.replace('</head>', `${previewStyles}</head>`);
    }
    return `${previewStyles}${html}`;
  }, [html]);

  const updateDesktopHeight = useCallback(() => {
    const iframe = desktopIframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) return;

      const body = doc.body;
      const firstChild = body.firstElementChild as HTMLElement | null;

      const contentHeight = Math.max(
        body.scrollHeight || 0,
        body.offsetHeight || 0,
        firstChild ? firstChild.scrollHeight || 0 : 0,
        firstChild ? firstChild.offsetHeight || 0 : 0
      );

      if (contentHeight > 50) {
        const finalHeight = Math.max(contentHeight + 10, 520);
        setDesktopHeight(finalHeight);
      }
    } catch {
      // Cross-origin fallback
    }
  }, []);

  const handleDesktopIframeLoad = useCallback(() => {
    updateDesktopHeight();

    const iframe = desktopIframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) return;

      if (typeof ResizeObserver !== 'undefined' && doc.body) {
        const ro = new ResizeObserver(() => {
          updateDesktopHeight();
        });
        ro.observe(doc.body);
      }

      const imgs = doc.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', updateDesktopHeight, { once: true });
          img.addEventListener('error', updateDesktopHeight, { once: true });
        }
      });

      setTimeout(updateDesktopHeight, 150);
      setTimeout(updateDesktopHeight, 500);
      setTimeout(updateDesktopHeight, 1500);
    } catch {
      // Ignore cross-origin issues
    }
  }, [updateDesktopHeight]);

  useEffect(() => {
    if (device === 'desktop') {
      updateDesktopHeight();
      const timer = setTimeout(updateDesktopHeight, 200);
      return () => clearTimeout(timer);
    }
  }, [device, updateDesktopHeight]);

  const previewSrc =
    !html && templateId
      ? apiUrl(`/api/render-email/${templateId}?preview=true`)
      : undefined;

  const openInNewTab = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const handleCopyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      trackEvent('html_exported', {
        template_id: templateId || templateSlug,
        category,
        source: 'template_preview_copy',
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
    }
  };

  return (
    <div className="flex flex-1 flex-col w-full">
      {/* ── Unified Sticky Template Subheader Toolbar ── */}
      <div className="sticky top-16 z-30 w-full border-b border-pb-slate-200/90 bg-white/95 backdrop-blur-md shadow-xs transition-colors dark:border-pb-white/[0.08] dark:bg-[#0b1329]/95">
        <div className="mx-auto flex h-16 max-w-7xl 2xl:max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* LEFT: Back Button + Template Title & Category */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Link
              href="/template"
              className="group flex items-center gap-2 rounded-xl border border-pb-slate-200 bg-pb-slate-50/90 px-3 py-1.5 text-xs sm:text-sm font-semibold text-pb-slate-700 shadow-xs transition-all hover:border-pb-blue-500/50 hover:bg-pb-white hover:text-pb-blue-600 dark:border-pb-white/10 dark:bg-pb-white/[0.04] dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.08] dark:hover:text-pb-white cursor-pointer shrink-0"
              aria-label="Back to all email templates"
            >
              <ArrowLeft size={15} className="transition-transform group-hover:-translate-x-0.5" />
              <span>Templates</span>
            </Link>

            <div className="h-5 w-px bg-pb-slate-200 dark:bg-pb-white/10 shrink-0" />

            <div className="flex items-center gap-2.5 min-w-0">
              <div className="hidden sm:flex h-8 w-8 items-center justify-center rounded-lg bg-pb-blue-50 text-pb-blue-600 dark:bg-pb-blue-500/10 dark:text-pb-blue-400 shrink-0">
                <LayoutTemplate size={16} />
              </div>
              <div className="min-w-0">
                <h1 className="text-xs sm:text-sm font-bold text-pb-slate-900 dark:text-white truncate max-w-[160px] sm:max-w-xs md:max-w-sm lg:max-w-md">
                  {templateName}
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  {category && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-pb-blue-600 dark:text-pb-blue-400">
                      {category}
                    </span>
                  )}
                  {isPremium && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 px-2 py-0.2 text-[9px] font-extrabold text-amber-600 dark:text-amber-400">
                      ★ PRO
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CENTER: Device Switcher (Desktop / Tablet / Mobile) */}
          <div className="hidden md:flex items-center rounded-xl border border-pb-slate-200/90 bg-pb-slate-100/90 p-1 shadow-xs dark:border-pb-white/10 dark:bg-pb-slate-900/90">
            <button
              type="button"
              onClick={() => setDevice('desktop')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                device === 'desktop'
                  ? 'bg-pb-blue-600 text-pb-white shadow-xs dark:bg-pb-blue-500'
                  : 'text-pb-slate-600 hover:text-pb-slate-900 dark:text-pb-slate-400 dark:hover:text-pb-white'
              }`}
              aria-label="Switch to desktop preview"
            >
              <Monitor size={14} />
              <span>Desktop</span>
            </button>
            <button
              type="button"
              onClick={() => setDevice('tablet')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                device === 'tablet'
                  ? 'bg-pb-blue-600 text-pb-white shadow-xs dark:bg-pb-blue-500'
                  : 'text-pb-slate-600 hover:text-pb-slate-900 dark:text-pb-slate-400 dark:hover:text-pb-white'
              }`}
              aria-label="Switch to tablet preview"
            >
              <Tablet size={14} />
              <span>Tablet</span>
            </button>
            <button
              type="button"
              onClick={() => setDevice('mobile')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                device === 'mobile'
                  ? 'bg-pb-blue-600 text-pb-white shadow-xs dark:bg-pb-blue-500'
                  : 'text-pb-slate-600 hover:text-pb-slate-900 dark:text-pb-slate-400 dark:hover:text-pb-white'
              }`}
              aria-label="Switch to mobile preview"
            >
              <Smartphone size={14} />
              <span>Mobile</span>
            </button>
          </div>

          {/* RIGHT: Actions (Copy HTML, Open Raw) + Primary "Use Template" CTA */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Copy HTML Button */}
            <button
              type="button"
              onClick={handleCopyHtml}
              className={`hidden sm:flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                copied
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700/50 dark:bg-emerald-950/40 dark:text-emerald-400'
                  : 'border-pb-slate-200 bg-white text-pb-slate-700 hover:border-pb-blue-500/50 hover:bg-pb-slate-50 hover:text-pb-blue-600 dark:border-pb-white/10 dark:bg-pb-slate-900 dark:text-pb-slate-300 dark:hover:bg-pb-white/5'
              }`}
              title="Copy template HTML to clipboard"
            >
              {copied ? (
                <>
                  <Check size={14} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copy HTML</span>
                </>
              )}
            </button>

            {/* Open Raw in New Tab */}
            <button
              type="button"
              onClick={openInNewTab}
              title="Open raw email in new tab"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-pb-slate-200 bg-white text-pb-slate-600 transition-colors hover:border-pb-slate-300 hover:bg-pb-slate-50 hover:text-pb-slate-900 dark:border-pb-white/10 dark:bg-pb-slate-900 dark:text-pb-slate-400 dark:hover:bg-pb-white/5 dark:hover:text-pb-white cursor-pointer"
              aria-label="Open raw email in new tab"
            >
              <ExternalLink size={14} />
            </button>

            {/* Primary "Use Template" CTA Button */}
            <a
              href={editorUrl}
              onClick={() => {
                trackEvent('template_use', {
                  template_id: templateId || templateSlug,
                  category,
                  source: 'template_preview_header',
                  destination: editorUrl,
                });
              }}
              className={`group flex items-center gap-2 rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-bold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-lg cursor-pointer shrink-0 ${
                isPremium
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/25 hover:shadow-amber-500/35'
                  : 'bg-gradient-to-r from-pb-blue-600 to-indigo-600 shadow-pb-blue-500/25 hover:shadow-pb-blue-500/35'
              }`}
            >
              {isPremium && <Lock size={13} className="shrink-0" />}
              <span>{isPremium ? 'Unlock with Pro' : 'Use Template'}</span>
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        {/* Mobile Viewport Toolbar (Shows on mobile screens < md) */}
        <div className="flex md:hidden items-center justify-between border-t border-pb-slate-200/70 px-4 py-2 bg-pb-slate-50/80 dark:border-pb-white/5 dark:bg-pb-white/[0.02]">
          <div className="flex items-center rounded-lg border border-pb-slate-200/90 bg-white p-0.5 dark:border-pb-white/10 dark:bg-pb-slate-900">
            <button
              type="button"
              onClick={() => setDevice('desktop')}
              className={`rounded-md px-2.5 py-1 text-xs font-bold transition-all ${
                device === 'desktop'
                  ? 'bg-pb-blue-600 text-white'
                  : 'text-pb-slate-600 dark:text-pb-slate-400'
              }`}
            >
              Desktop
            </button>
            <button
              type="button"
              onClick={() => setDevice('tablet')}
              className={`rounded-md px-2.5 py-1 text-xs font-bold transition-all ${
                device === 'tablet'
                  ? 'bg-pb-blue-600 text-white'
                  : 'text-pb-slate-600 dark:text-pb-slate-400'
              }`}
            >
              Tablet
            </button>
            <button
              type="button"
              onClick={() => setDevice('mobile')}
              className={`rounded-md px-2.5 py-1 text-xs font-bold transition-all ${
                device === 'mobile'
                  ? 'bg-pb-blue-600 text-white'
                  : 'text-pb-slate-600 dark:text-pb-slate-400'
              }`}
            >
              Mobile
            </button>
          </div>

          <button
            type="button"
            onClick={handleCopyHtml}
            className="flex items-center gap-1.5 rounded-lg border border-pb-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-pb-slate-700 dark:border-pb-white/10 dark:bg-pb-slate-900 dark:text-pb-slate-300"
          >
            {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>

      {/* ── Viewport Canvas Area with Real Devices ── */}
      <main className="relative flex flex-1 w-full items-start justify-center overflow-y-auto bg-[#eef2f7] p-3 sm:p-6 lg:p-8 dark:bg-[#070b14] custom-scrollbar min-h-[calc(100vh-8rem)]">
        {/* ── 1. REAL DESKTOP BROWSER FRAME ── */}
        {device === 'desktop' && (
          <div className="flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-pb-slate-300/80 bg-white shadow-2xl dark:border-pb-white/10 dark:bg-slate-900 transition-all duration-300">
            {/* macOS Browser Chrome Bar */}
            <div className="flex h-11 shrink-0 items-center justify-between border-b border-pb-slate-200/90 bg-[#f1f5f9] px-4 dark:border-pb-white/10 dark:bg-[#0b1329]">
              {/* Traffic lights */}
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-[#ff5f56] border border-[#e0443e] shadow-xs" />
                <span className="h-3 w-3 rounded-full bg-[#ffbd2e] border border-[#dea123] shadow-xs" />
                <span className="h-3 w-3 rounded-full bg-[#27c93f] border border-[#1aab29] shadow-xs" />
                <div className="hidden sm:flex items-center gap-1 pl-3 text-pb-slate-400">
                  <ChevronLeft size={14} className="cursor-default opacity-50" />
                  <ChevronRight size={14} className="cursor-default opacity-30" />
                </div>
              </div>

              {/* URL / Address Bar */}
              <div className="flex h-7 w-60 sm:w-80 md:w-96 items-center justify-center gap-1.5 rounded-lg border border-pb-slate-200/90 bg-white px-3 text-[11px] font-medium text-pb-slate-600 shadow-xs dark:border-pb-white/10 dark:bg-[#070d1d] dark:text-pb-slate-300">
                <Lock size={11} className="text-emerald-500 shrink-0" />
                <span className="truncate">mail.postbrix.com/inbox/{templateSlug}</span>
                <RotateCw size={11} className="ml-auto text-pb-slate-400 shrink-0 opacity-70" />
              </div>

              {/* Viewport badge */}
              <div className="flex items-center gap-2">
                <span className="hidden md:inline-flex rounded-md bg-pb-slate-200/80 px-2 py-0.5 text-[10px] font-bold text-pb-slate-600 dark:bg-pb-white/10 dark:text-pb-slate-300">
                  Desktop • 1200px
                </span>
              </div>
            </div>

            {/* Email Client Header Bar */}
            <div className="flex shrink-0 items-center justify-between border-b border-pb-slate-100 bg-[#f8fafc] px-4 py-2 text-xs text-pb-slate-600 dark:border-pb-white/[0.06] dark:bg-[#0d162d] dark:text-pb-slate-300">
              <div className="flex items-center gap-2.5 truncate">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-pb-blue-600 text-white font-bold text-[10px] shrink-0">
                  PB
                </div>
                <div className="truncate">
                  <span className="font-bold text-pb-slate-900 dark:text-white truncate">
                    {templateName}
                  </span>
                  <span className="hidden sm:inline text-pb-slate-400 text-[11px] ml-2">
                    &lt;templates@postbrix.com&gt;
                  </span>
                </div>
              </div>
              <span className="text-[10px] font-medium text-pb-slate-400 shrink-0">Today, 10:24 AM</span>
            </div>

            {/* Full-Height Desktop Iframe Viewport */}
            <div
              className="relative w-full bg-white transition-[height] duration-200"
              style={{ minHeight: `${desktopHeight}px`, height: `${desktopHeight}px` }}
            >
              <iframe
                ref={desktopIframeRef}
                onLoad={handleDesktopIframeLoad}
                {...(enhancedHtml ? { srcDoc: enhancedHtml } : { src: previewSrc })}
                style={{
                  height: `${desktopHeight}px`,
                  minHeight: `${desktopHeight}px`,
                }}
                className="w-full border-none bg-white transition-[height] duration-200"
                title={`${templateName} Desktop Preview`}
                sandbox="allow-same-origin allow-scripts"
                scrolling="no"
              />
            </div>
          </div>
        )}

        {/* ── 2. REAL TABLET / IPAD FRAME ── */}
        {device === 'tablet' && (
          <div className="relative mx-auto flex w-full max-w-[680px] flex-col overflow-hidden rounded-[2.5rem] border-[14px] border-[#1e293b] bg-[#1e293b] shadow-2xl ring-1 ring-black/20 dark:border-[#1e293b] dark:ring-white/10 transition-all duration-300">
            {/* Top Bezel with Camera Dot */}
            <div className="absolute top-0 left-0 right-0 z-20 flex h-[14px] -translate-y-2 items-center justify-center pointer-events-none">
              <div className="h-2 w-2 rounded-full bg-[#0a0f1d] ring-1 ring-white/20 flex items-center justify-center">
                <span className="h-1 w-1 rounded-full bg-blue-500/50" />
              </div>
            </div>

            {/* Inner Tablet Screen */}
            <div className="relative flex flex-col overflow-hidden rounded-[1.75rem] bg-white shadow-inner">
              {/* iPadOS Status Bar */}
              <div className="flex h-7 w-full shrink-0 items-center justify-between bg-pb-slate-100 px-5 text-[11px] font-semibold text-pb-slate-700 dark:bg-pb-slate-800 dark:text-pb-slate-200">
                <span>9:41 AM  Wed Sep 25</span>
                <div className="flex items-center gap-2">
                  <Wifi size={12} />
                  <span className="text-[10px] font-bold">100%</span>
                  <Battery size={13} className="fill-current" />
                </div>
              </div>

              {/* Tablet Iframe */}
              <div className="relative h-[620px] lg:h-[680px] w-full bg-white">
                <iframe
                  {...(enhancedHtml ? { srcDoc: enhancedHtml } : { src: previewSrc })}
                  className="h-full w-full border-none bg-white"
                  title={`${templateName} Tablet Preview`}
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>

              {/* iPad Home Indicator Bar */}
              <div className="flex h-5 w-full shrink-0 items-center justify-center bg-white">
                <span className="h-1 w-36 rounded-full bg-pb-slate-300 dark:bg-pb-slate-400" />
              </div>
            </div>
          </div>
        )}

        {/* ── 3. REAL MOBILE / IPHONE 16 PRO FRAME ── */}
        {device === 'mobile' && (
          <div className="relative mx-auto flex w-full max-w-[375px] flex-col rounded-[3.25rem] border-[12px] border-[#0f172a] bg-[#0f172a] shadow-2xl ring-1 ring-black/30 dark:border-[#0f172a] dark:ring-white/10 transition-all duration-300">
            {/* Left Side Physical Buttons */}
            <div className="absolute -left-[14px] top-24 h-7 w-[3px] rounded-l-md bg-pb-slate-600 hidden sm:block" />
            <div className="absolute -left-[14px] top-36 h-11 w-[3px] rounded-l-md bg-pb-slate-600 hidden sm:block" />
            <div className="absolute -left-[14px] top-50 h-11 w-[3px] rounded-l-md bg-pb-slate-600 hidden sm:block" />
            {/* Right Side Power Button */}
            <div className="absolute -right-[14px] top-36 h-16 w-[3px] rounded-r-md bg-pb-slate-600 hidden sm:block" />

            {/* Inner Phone Screen */}
            <div className="relative flex flex-col overflow-hidden rounded-[2.5rem] bg-white shadow-inner">
              {/* iOS Status Bar with Dynamic Island */}
              <div className="relative flex h-11 w-full shrink-0 items-center justify-between px-6 pt-1 text-[12px] font-bold text-pb-slate-900 bg-white z-20">
                <span className="tracking-tight text-[13px] font-semibold pl-1">9:41</span>

                {/* Dynamic Island */}
                <div className="absolute left-1/2 top-2 z-30 flex h-6 w-24 -translate-x-1/2 items-center justify-between rounded-full bg-black px-2.5 shadow-md">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#111] ring-1 ring-white/10 flex items-center justify-center">
                    <span className="h-1 w-1 rounded-full bg-blue-500/40" />
                  </span>
                  <span className="h-2 w-2 rounded-full bg-[#0a0a0a]" />
                </div>

                <div className="flex items-center gap-1.5 pr-1">
                  <Signal size={12} className="stroke-[2.5]" />
                  <span className="text-[10px] font-bold">5G</span>
                  <Battery size={14} className="fill-current stroke-[2]" />
                </div>
              </div>

              {/* Mobile Iframe */}
              <div className="relative h-[600px] sm:h-[660px] w-full bg-white">
                <iframe
                  {...(enhancedHtml ? { srcDoc: enhancedHtml } : { src: previewSrc })}
                  className="h-full w-full border-none bg-white"
                  title={`${templateName} Mobile Preview`}
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>

              {/* iOS Home Indicator Bar */}
              <div className="flex h-5 w-full shrink-0 items-center justify-center bg-white z-20">
                <span className="h-1 w-32 rounded-full bg-pb-slate-300 dark:bg-pb-slate-400" />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── Bottom Feature & Action Bar ── */}
      <div className="w-full shrink-0 border-t border-outline-variant bg-pb-white dark:border-pb-white/10 dark:bg-[#0b1329]">
        <div className="mx-auto flex h-14 max-w-7xl 2xl:max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Verification badges */}
          <div className="hidden sm:flex items-center gap-6 text-xs text-pb-slate-500 dark:text-pb-slate-400">
            <span className="flex items-center gap-1.5 font-medium">
              <Zap size={14} className="text-amber-500" />
              100% Responsive MJML
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <FileCode size={14} className="text-emerald-500" />
              Production-Ready HTML
            </span>
            <span className="hidden lg:flex items-center gap-1.5 font-medium">
              <CheckCircle2 size={14} className="text-pb-blue-500" />
              ESP Compatible: Klaviyo · Mailchimp · SendGrid · Resend
            </span>
          </div>

          {/* Secondary Customize CTA */}
          <div className="flex w-full sm:w-auto items-center justify-end gap-3">
            <a
              href={editorUrl}
              className={`group flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-6 py-2 text-xs md:text-sm font-bold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-lg cursor-pointer ${
                isPremium
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/25 hover:shadow-amber-500/35'
                  : 'bg-gradient-to-r from-pb-blue-600 to-indigo-600 shadow-pb-blue-500/25 hover:shadow-pb-blue-500/35'
              }`}
            >
              {isPremium ? (
                <>
                  <Lock size={14} />
                  <span>Customize with Pro</span>
                </>
              ) : (
                <>
                  <Sparkles size={14} className="transition-transform group-hover:rotate-12" />
                  <span>Customize This Template</span>
                </>
              )}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
